/**
 * @fileoverview Pantalla 4 — Votación. Pol.is style.
 * Encabezado con brecha actual + statement card animada + 3 botones de
 * voto + barra de progreso + navegación + salida segura.
 */

import { ChevronLeft, ChevronRight, LogOut } from "lucide-react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";

import { AppShell } from "../components/AppShell";
import { LoadingSpinner } from "../components/LoadingSpinner";
import { ProgressBar } from "../components/ProgressBar";
import { StatementCard } from "../components/StatementCard";
import { VoteButtons } from "../components/VoteButtons";
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from "../components/ui/alert-dialog";
import { Button } from "../components/ui/button";
import { useParticipacion } from "../context/ParticipacionContext";
import { useBrechas } from "../hooks/useBrechas";
import { useRequireSession } from "../hooks/useSession";
import { useVotos } from "../hooks/useVotos";

export function Votacion() {
    useRequireSession();
    const navigate = useNavigate();
    const { state, dispatch, statementsPlanas, brechaDeStatement } = useParticipacion();
    const { cargando } = useBrechas();
    const { enviarVoto, enviando } = useVotos();

    const [valorEnviando, setValorEnviando] = useState(
        /** @type {"acuerdo"|"desacuerdo"|"pasar"|null} */ (null)
    );
    const [confirmandoSalida, setConfirmandoSalida] = useState(false);
    const [mensajeError, setMensajeError] = useState(/** @type {string | null} */ (null));

    const total = statementsPlanas.length;
    const idx = state.indice_actual;
    const statement = statementsPlanas[idx];
    const brecha = brechaDeStatement(idx);
    const votoActual = statement ? state.votos[statement.id] || null : null;

    /**
     * @param {"acuerdo"|"desacuerdo"|"pasar"} valor
     */
    const handleVotar = useCallback(
        async (valor) => {
            if (!statement || enviando) return;
            setMensajeError(null);
            setValorEnviando(valor);

            const eraVotoNuevo = !state.votos[statement.id];
            const ok = await enviarVoto(statement.id, valor);
            setValorEnviando(null);

            if (!ok) {
                setMensajeError("No pudimos guardar tu voto. Intenta de nuevo.");
                return;
            }

            // Auto-avance solo si es voto nuevo (no si está corrigiendo)
            if (eraVotoNuevo) {
                window.setTimeout(() => {
                    if (idx < total - 1) {
                        dispatch({ type: "SET_INDICE", payload: idx + 1 });
                    } else {
                        navigate("/respuesta-abierta");
                    }
                }, 400);
            }
        },
        [statement, enviando, state.votos, enviarVoto, idx, total, dispatch, navigate]
    );

    function handleAnterior() {
        if (idx > 0) dispatch({ type: "SET_INDICE", payload: idx - 1 });
    }

    /**
     * Devuelve true si el participante puede avanzar a la siguiente
     * pregunta o pasar a la respuesta abierta. Bloqueamos avance
     * cuando aún no hay voto en la pregunta actual (M3: validación
     * obligatoria antes de avanzar). Excepción: el botón "Terminar"
     * de la última pregunta requiere voto igual que las demás.
     */
    const puedeAvanzar = Boolean(votoActual) && !enviando;

    function handleSiguiente() {
        if (!puedeAvanzar) return;
        if (idx < total - 1) {
            dispatch({ type: "SET_INDICE", payload: idx + 1 });
        } else {
            navigate("/respuesta-abierta");
        }
    }

    // Atajos de teclado: A acuerdo, D desacuerdo, P pasar, ←/→ nav
    useEffect(() => {
        function onKey(/** @type {KeyboardEvent} */ e) {
            if (e.target instanceof HTMLInputElement || e.target instanceof HTMLTextAreaElement) {
                return;
            }
            if (e.key === "a" || e.key === "A") handleVotar("acuerdo");
            else if (e.key === "d" || e.key === "D") handleVotar("desacuerdo");
            else if (e.key === "p" || e.key === "P") handleVotar("pasar");
            else if (e.key === "ArrowLeft") handleAnterior();
            else if (e.key === "ArrowRight") {
                // M3: solo avanzar si la pregunta actual tiene voto.
                if (statement && state.votos[statement.id]) handleSiguiente();
            }
        }
        window.addEventListener("keydown", onKey);
        return () => window.removeEventListener("keydown", onKey);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [idx, statement, state.votos]);

    const totalVotos = useMemo(() => Object.keys(state.votos).length, [state.votos]);

    if (cargando || statementsPlanas.length === 0) {
        return (
            <AppShell testid="page-votacion-loading">
                <div className="flex flex-col items-center justify-center py-24 gap-3 text-muted-foreground">
                    <LoadingSpinner size={28} />
                    <p>Cargando las afirmaciones...</p>
                </div>
            </AppShell>
        );
    }

    if (!statement || !brecha) return null;

    return (
        <AppShell testid="page-votacion">
            <div className="space-y-8">
                <header className="text-center" data-testid="que-opinas-header">
                    <h1 className="font-serif-headline text-3xl sm:text-4xl lg:text-5xl font-semibold text-foreground">
                        ¿Qué opinas?
                    </h1>
                </header>

                <StatementCard
                    id={statement.id}
                    codigo={statement.codigo}
                    texto={statement.texto}
                />

                <VoteButtons
                    onVotar={handleVotar}
                    seleccionado={/** @type {any} */ (votoActual)}
                    enviando={enviando}
                    valorEnviando={valorEnviando}
                />

                {mensajeError ? (
                    <p
                        role="alert"
                        className="text-sm text-destructive bg-destructive/10 rounded-lg p-3"
                        data-testid="vote-error"
                    >
                        {mensajeError}
                    </p>
                ) : null}

                <div className="space-y-3">
                    <ProgressBar
                        actual={idx}
                        total={total}
                        etiqueta={`Afirmación ${idx + 1} de ${total} · ${totalVotos} contestadas`}
                    />
                    <div className="flex items-center justify-between gap-3">
                        <Button
                            variant="ghost"
                            size="sm"
                            onClick={handleAnterior}
                            disabled={idx === 0 || enviando}
                            data-testid="anterior-btn"
                        >
                            <ChevronLeft size={16} className="mr-1" /> Anterior
                        </Button>
                        <Button
                            variant="ghost"
                            size="sm"
                            onClick={handleSiguiente}
                            disabled={!puedeAvanzar}
                            aria-label={
                                votoActual
                                    ? idx === total - 1
                                        ? "Terminar"
                                        : "Siguiente"
                                    : "Selecciona una opción para continuar"
                            }
                            data-testid="siguiente-btn"
                        >
                            {idx === total - 1 ? "Terminar" : "Siguiente"}
                            <ChevronRight size={16} className="ml-1" />
                        </Button>
                    </div>
                </div>

                <div className="border-t border-border pt-4 flex justify-center">
                    <button
                        type="button"
                        onClick={() => setConfirmandoSalida(true)}
                        className="text-xs text-muted-foreground hover:text-foreground inline-flex items-center gap-1.5"
                        data-testid="salir-btn"
                    >
                        <LogOut size={14} />
                        Salir y guardar lo que llevo
                    </button>
                </div>
            </div>

            <AlertDialog open={confirmandoSalida} onOpenChange={setConfirmandoSalida}>
                <AlertDialogContent data-testid="confirmar-salida-dialog">
                    <AlertDialogHeader>
                        <AlertDialogTitle>¿Estás seguro?</AlertDialogTitle>
                        <AlertDialogDescription>
                            Tus respuestas se guardan automáticamente. Puedes volver con tu mismo
                            código <strong>{state.codigo}</strong> a continuar antes de que cierre el
                            foro.
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel data-testid="quedarme-btn">
                            Quedarme
                        </AlertDialogCancel>
                        <AlertDialogAction
                            onClick={() => navigate("/salida")}
                            data-testid="confirmar-salir-btn"
                        >
                            Sí, salir
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </AppShell>
    );
}
